'use client'

import { useAtomValue, useStore } from 'jotai'
import { useCallback } from 'react'

import {
  buildExplainWidgetContext,
  type BuildExplainWidgetContextInput,
} from '@/features/data/components/widgets/content/amberdata/widget-explainer-context'
import type { CustomUIDataTypes } from '@/lib/types'
import { randomUUID } from '@/lib/utils'
import {
  classifyResearchMessage,
  postExplainWidget,
  postFollowupResearch,
  postResearchQuestion,
  type ResearchFollowupMode,
  startProposedFollowup,
  startProposedResearch,
} from '@/service/research.api'
import {
  messagesAtomFamily,
  type PendingResearchAction,
  researchConversationAtomFamily,
  researchFollowupTargetAtomFamily,
  threadMetaAtomFamily,
  widgetExplainPendingAtomFamily,
} from '@/state/chat'
import {
  activeResearchJobAtomFamily,
  isResearchJobActive,
  type ResearchJob,
  researchJobsAtom,
} from '@/state/research'

const START_FAILED_TEXT = 'Could not start research. Please try again.'
export const INITIAL_RESEARCH_STATUS_TEXT = 'Researching…'
export const CLASSIFYING_STATUS_TEXT = 'Classifying…'

interface StartOptions {
  clientRequestId?: string
  kind: ResearchJob['kind']
  question: string
  submit: () => Promise<{ jobId: string }>
  parentJobId?: string
  followupMode?: ResearchFollowupMode
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : START_FAILED_TEXT
}

function researchStatusParts(text: string): CustomUIDataTypes[] {
  return [{ type: 'component', componentType: 'RESEARCH_STATUS', data: { text } }]
}

export function useResearchManager(threadId: string) {
  const store = useStore()
  const activeJob = useAtomValue(activeResearchJobAtomFamily(threadId))
  const conversation = useAtomValue(researchConversationAtomFamily(threadId))

  // ─── Legacy path (flag off, or widget-explain which never classifies) ──────────────────────
  // Pushes the user turn + an assistant placeholder in one shot, exactly as the single `start()`
  // this was split from used to (see the "composed-hugging-hammock" plan, Task 3) — kept as one
  // function for this path since there's no intermediate "wait for a tap" step here.

  const appendResearchTurn = useCallback(
    (question: string, assistantParts: CustomUIDataTypes[]): string => {
      const assistantMessageId = `research-msg-${randomUUID()}`
      store.set(messagesAtomFamily(threadId), (messages) => [
        ...messages,
        {
          id: `research-user-${randomUUID()}`,
          role: 'user',
          parts: [{ type: 'text', text: question }],
        },
        { id: assistantMessageId, role: 'assistant', parts: assistantParts },
      ])
      const meta = store.get(threadMetaAtomFamily(threadId))
      if (meta) {
        store.set(threadMetaAtomFamily(threadId), {
          ...meta,
          title: meta.title || question,
          updatedAt: Date.now(),
        })
      }
      return assistantMessageId
    },
    [store, threadId]
  )

  const hasActiveJob = useCallback(() => {
    const jobs = store.get(researchJobsAtom)
    return Object.values(jobs).some((job) => job.threadId === threadId && isResearchJobActive(job))
  }, [store, threadId])

  // Creates the ResearchJob record optimistically (phase STARTING, no jobId yet) BEFORE the
  // network call resolves, then updates it in place once `submit()` settles. Correct for this
  // path only — there's no proposal to expire out from under an optimistic job. The new
  // confirm-card path (confirmResearchProposal below) deliberately does the opposite: it waits
  // for the proposal-aware POST to actually succeed before a ResearchJob is created at all, so a
  // 409/expired proposal never leaves a FAILED job behind for a run that never started.
  const createAndSubmitResearchJob = useCallback(
    (assistantMessageId: string, options: StartOptions): boolean => {
      const jobs = store.get(researchJobsAtom)
      if (hasActiveJob()) return false

      const clientRequestId = options.clientRequestId ?? randomUUID()
      if (jobs[clientRequestId]) return false

      const now = Date.now()
      const parent = options.parentJobId
        ? Object.values(jobs).find((job) => job.jobId === options.parentJobId)
        : undefined
      const job: ResearchJob = {
        id: clientRequestId,
        jobId: null,
        clientRequestId,
        threadId,
        kind: options.kind,
        parentJobId: options.parentJobId,
        rootJobId: parent?.rootJobId ?? parent?.jobId ?? undefined,
        followupMode: options.followupMode,
        question: options.question,
        assistantMessageId,
        phase: 'STARTING',
        events: [],
        report: null,
        error: null,
        startedAt: now,
        updatedAt: now,
        completedAt: null,
        notifiedAt: null,
        viewedAt: null,
      }

      store.set(researchJobsAtom, { ...jobs, [clientRequestId]: job })
      store.set(widgetExplainPendingAtomFamily(threadId), options.kind === 'widget-explain')
      store.set(messagesAtomFamily(threadId), (messages) =>
        messages.map((entry) =>
          entry.id === assistantMessageId
            ? { ...entry, parts: researchStatusParts(INITIAL_RESEARCH_STATUS_TEXT) }
            : entry
        )
      )

      void options.submit().then(
        ({ jobId }) => {
          store.set(researchJobsAtom, (current) => {
            const existing = current[clientRequestId]
            if (!existing) return current
            return {
              ...current,
              [clientRequestId]: {
                ...existing,
                jobId,
                rootJobId: existing.rootJobId ?? jobId,
                phase: 'PENDING',
                updatedAt: Date.now(),
              },
            }
          })
        },
        (error) => {
          const message = errorMessage(error)
          const completedAt = Date.now()
          store.set(researchJobsAtom, (current) => ({
            ...current,
            [clientRequestId]: {
              ...current[clientRequestId],
              phase: 'FAILED',
              error: message,
              updatedAt: completedAt,
              completedAt,
            },
          }))
          store.set(messagesAtomFamily(threadId), (messages) =>
            messages.map((entry) =>
              entry.id === assistantMessageId
                ? { ...entry, parts: [{ type: 'text', text: message }] }
                : entry
            )
          )
          store.set(widgetExplainPendingAtomFamily(threadId), false)
        }
      )
      return true
    },
    [store, threadId, hasActiveJob]
  )

  const startQuestion = useCallback(
    (question: string) => {
      if (hasActiveJob()) return false
      const targetAtom = researchFollowupTargetAtomFamily(threadId)
      const followup = store.get(targetAtom)
      const assistantMessageId = appendResearchTurn(
        question,
        researchStatusParts(INITIAL_RESEARCH_STATUS_TEXT)
      )
      const started = createAndSubmitResearchJob(assistantMessageId, {
        kind: followup ? 'followup' : 'question',
        question,
        parentJobId: followup?.parentJobId,
        followupMode: followup?.mode,
        submit: () =>
          followup
            ? postFollowupResearch(followup.parentJobId, question, followup.mode)
            : postResearchQuestion(question),
      })
      if (started && followup) store.set(targetAtom, null)
      return started
    },
    [store, threadId, hasActiveJob, appendResearchTurn, createAndSubmitResearchJob]
  )

  const startWidgetExplain = useCallback(
    (request: { requestId: string; label: string; input: BuildExplainWidgetContextInput }) => {
      if (hasActiveJob()) return false
      const widget = buildExplainWidgetContext(request.input, Date.now())
      if (!widget) return false
      const question = `Explain ${request.label}`
      const assistantMessageId = appendResearchTurn(
        question,
        researchStatusParts(INITIAL_RESEARCH_STATUS_TEXT)
      )
      return createAndSubmitResearchJob(assistantMessageId, {
        clientRequestId: request.requestId,
        kind: 'widget-explain',
        question,
        submit: () => postExplainWidget(widget),
      })
    },
    [hasActiveJob, appendResearchTurn, createAndSubmitResearchJob]
  )

  // ─── Classify/confirm-card path (behind RESEARCH_CLASSIFY_ENABLED — see
  // features/order/research/research-classify-flag.ts). Callers (assistant-runtime-provider.tsx
  // for the initial send, the proposal/refusal/error cards for confirm/cancel/retry/run-anyway)
  // branch on the flag themselves; these functions don't re-check it, matching how startQuestion
  // above doesn't know about chatMode either — routing is the caller's job.

  const replaceMessageParts = useCallback(
    (assistantMessageId: string, parts: CustomUIDataTypes[]) => {
      store.set(messagesAtomFamily(threadId), (messages) =>
        messages.map((entry) => (entry.id === assistantMessageId ? { ...entry, parts } : entry))
      )
    },
    [store, threadId]
  )

  // Shared by the initial send, "run anyway", and error-card Retry — all three are "run classify
  // against this existing assistant message" with a fresh attempt token, never a new user
  // message. Keeping this as one function is what stops "run anyway" from accidentally
  // re-appending the user's turn (see the plan's Task 5).
  const runClassifyAttempt = useCallback(
    async (params: { assistantMessageId: string; question: string; userOptIn: boolean }) => {
      const attemptId = randomUUID()
      const conversationAtom = researchConversationAtomFamily(threadId)
      const serverThreadId = store.get(conversationAtom).serverThreadId
      store.set(conversationAtom, (prev) => ({
        ...prev,
        classifying: true,
        classifyAttemptId: attemptId,
        pendingAction: undefined,
      }))
      replaceMessageParts(params.assistantMessageId, researchStatusParts(CLASSIFYING_STATUS_TEXT))

      const isStale = () => store.get(conversationAtom).classifyAttemptId !== attemptId

      try {
        const result = await classifyResearchMessage(params.question, {
          threadId: serverThreadId,
        })
        if (isStale()) return

        store.set(conversationAtom, (prev) => ({
          ...prev,
          classifying: false,
          serverThreadId: result.threadId || prev.serverThreadId,
        }))

        switch (result.type) {
          case 'job': {
            const jobs = store.get(researchJobsAtom)
            if (hasActiveJob()) {
              replaceMessageParts(params.assistantMessageId, [
                { type: 'text', text: 'Another research run is already in progress in this chat.' },
              ])
              break
            }
            const clientRequestId = randomUUID()
            const now = Date.now()
            const parent = result.parentJobId
              ? Object.values(jobs).find((job) => job.jobId === result.parentJobId)
              : undefined
            const job: ResearchJob = {
              id: clientRequestId,
              jobId: result.jobId,
              clientRequestId,
              threadId,
              kind: result.parentJobId ? 'followup' : 'question',
              parentJobId: result.parentJobId,
              rootJobId: parent?.rootJobId ?? parent?.jobId ?? result.jobId,
              followupMode: result.parentJobId ? 'report' : undefined,
              question: params.question,
              assistantMessageId: params.assistantMessageId,
              phase: 'PENDING',
              events: [],
              report: null,
              error: null,
              startedAt: now,
              updatedAt: now,
              completedAt: null,
              notifiedAt: null,
              viewedAt: null,
            }
            store.set(researchJobsAtom, { ...jobs, [clientRequestId]: job })
            replaceMessageParts(
              params.assistantMessageId,
              researchStatusParts(INITIAL_RESEARCH_STATUS_TEXT)
            )
            break
          }
          case 'answer':
            replaceMessageParts(params.assistantMessageId, [
              { type: 'text', text: result.answer.text },
            ])
            break
          case 'message':
            replaceMessageParts(params.assistantMessageId, [{ type: 'text', text: result.message }])
            break
        }
      } catch (error) {
        if (isStale()) return
        const message = errorMessage(error)
        const pendingAction: PendingResearchAction = {
          attemptId,
          serverRequestId: '',
          originalMessage: params.question,
          assistantMessageId: params.assistantMessageId,
          status: 'pending',
          data: { kind: 'error', message, userOptIn: params.userOptIn },
        }
        store.set(conversationAtom, (prev) => ({
          ...prev,
          classifying: false,
          pendingAction,
        }))
        replaceMessageParts(params.assistantMessageId, [
          {
            type: 'component',
            componentType: 'RESEARCH_CLASSIFY_ERROR_CARD',
            data: pendingAction as unknown as Record<string, unknown>,
          },
        ])
      }
    },
    [store, threadId, hasActiveJob, replaceMessageParts]
  )

  const submitResearchMessage = useCallback(
    (text: string) => {
      if (hasActiveJob()) return false
      const assistantMessageId = appendResearchTurn(
        text,
        researchStatusParts(CLASSIFYING_STATUS_TEXT)
      )
      void runClassifyAttempt({ assistantMessageId, question: text, userOptIn: false })
      return true
    },
    [hasActiveJob, appendResearchTurn, runClassifyAttempt]
  )

  const retryClassify = useCallback(
    (pendingAction: PendingResearchAction) => {
      if (pendingAction.data.kind !== 'error') return
      void runClassifyAttempt({
        assistantMessageId: pendingAction.assistantMessageId,
        question: pendingAction.originalMessage,
        userOptIn: pendingAction.data.userOptIn,
      })
    },
    [runClassifyAttempt]
  )

  const runAnyway = useCallback(
    (pendingAction: PendingResearchAction) => {
      if (pendingAction.data.kind !== 'refusal') return
      void runClassifyAttempt({
        assistantMessageId: pendingAction.assistantMessageId,
        question: pendingAction.originalMessage,
        userOptIn: true,
      })
    },
    [runClassifyAttempt]
  )

  const cancelResearchProposal = useCallback(
    (pendingAction: PendingResearchAction) => {
      const conversationAtom = researchConversationAtomFamily(threadId)
      const current = store.get(conversationAtom)
      if (current.pendingAction?.attemptId !== pendingAction.attemptId) return
      if (current.pendingAction.status !== 'pending') return

      store.set(conversationAtom, (prev) =>
        prev.pendingAction?.attemptId === pendingAction.attemptId
          ? { ...prev, pendingAction: undefined }
          : prev
      )
      replaceMessageParts(pendingAction.assistantMessageId, [
        { type: 'text', text: 'Research cancelled.' },
      ])
    },
    [store, threadId, replaceMessageParts]
  )

  const confirmResearchProposal = useCallback(
    async (pendingAction: PendingResearchAction) => {
      if (pendingAction.data.kind !== 'proposal') return
      const conversationAtom = researchConversationAtomFamily(threadId)
      const current = store.get(conversationAtom)
      if (current.pendingAction?.attemptId !== pendingAction.attemptId) return
      if (current.pendingAction.status !== 'pending') return

      store.set(conversationAtom, (prev) =>
        prev.pendingAction?.attemptId === pendingAction.attemptId
          ? { ...prev, pendingAction: { ...prev.pendingAction, status: 'confirming' } }
          : prev
      )

      const { route, proposal } = pendingAction.data
      const fail = (message: string) => {
        store.set(conversationAtom, (prev) =>
          prev.pendingAction?.attemptId === pendingAction.attemptId
            ? { ...prev, pendingAction: { ...prev.pendingAction, status: 'resolved' } }
            : prev
        )
        replaceMessageParts(pendingAction.assistantMessageId, [{ type: 'text', text: message }])
      }

      if (hasActiveJob()) {
        fail('Another research run is already in progress in this chat.')
        return
      }

      try {
        const kind: ResearchJob['kind'] = route === 'FULL_RESEARCH' ? 'question' : 'followup'
        let jobId: string
        if (route === 'FULL_RESEARCH') {
          ;({ jobId } = await startProposedResearch({
            question: pendingAction.originalMessage,
            proposalId: proposal.proposalId,
          }))
        } else {
          if (!proposal.parentJobId) {
            fail('This report can no longer be updated — please ask again.')
            return
          }
          ;({ jobId } = await startProposedFollowup({
            parentJobId: proposal.parentJobId,
            question: pendingAction.originalMessage,
            proposalId: proposal.proposalId,
            mode: 'report',
          }))
        }

        // Only now — after the proposal-aware POST actually succeeded — does a ResearchJob get
        // created. A 409 (expired/used proposal) or any other failure below never reaches here,
        // so it never leaves a FAILED job behind for a run that never started.
        const clientRequestId = randomUUID()
        const now = Date.now()
        const parent = proposal.parentJobId
          ? Object.values(store.get(researchJobsAtom)).find(
              (job) => job.jobId === proposal.parentJobId
            )
          : undefined
        const job: ResearchJob = {
          id: clientRequestId,
          jobId,
          clientRequestId,
          threadId,
          kind,
          parentJobId: proposal.parentJobId,
          rootJobId: parent?.rootJobId ?? parent?.jobId ?? jobId,
          followupMode: kind === 'followup' ? 'report' : undefined,
          question: pendingAction.originalMessage,
          assistantMessageId: pendingAction.assistantMessageId,
          phase: 'PENDING',
          events: [],
          report: null,
          error: null,
          startedAt: now,
          updatedAt: now,
          completedAt: null,
          notifiedAt: null,
          viewedAt: null,
        }
        store.set(researchJobsAtom, (jobs) => ({ ...jobs, [clientRequestId]: job }))
        replaceMessageParts(
          pendingAction.assistantMessageId,
          researchStatusParts(INITIAL_RESEARCH_STATUS_TEXT)
        )
        store.set(conversationAtom, (prev) =>
          prev.pendingAction?.attemptId === pendingAction.attemptId
            ? { ...prev, pendingAction: undefined }
            : prev
        )
      } catch (error) {
        fail(errorMessage(error))
      }
    },
    [store, threadId, hasActiveJob, replaceMessageParts]
  )

  return {
    activeJob,
    isActive: !!activeJob,
    conversation,
    hasPendingResearchAction:
      conversation.classifying ||
      conversation.pendingAction?.status === 'pending' ||
      conversation.pendingAction?.status === 'confirming',
    startQuestion,
    startWidgetExplain,
    submitResearchMessage,
    confirmResearchProposal,
    cancelResearchProposal,
    retryClassify,
    runAnyway,
  }
}
